// Parrainer une joueuse, une équipe engagée ou toute l'équipe (D-136, révise D-067 ; maquettes validées). Ce qui se
// choisit dans « Nouveau binôme » et ce qui s'écrit dessous et dans « Binômes en cours ». Le mot « groupe » reste aux
// groupes de bénévoles (D-101) : une équipe d'Arceau qui engage U18-1 et U18-2 est « U18 filles en entier ».
import { agree, type TeamGender } from './labels';

export interface MentorTeam { team_id: string; name: string; gender: TeamGender; squads: { id: string; name: string }[] }

/** Une cible possible : une équipe engagée (U18 filles · U18-1), une équipe sans équipes engagées (U13 filles), ou
 *  toute une équipe qui en a plusieurs (U18 filles en entier (U18-1 et U18-2)). */
export interface MentorTarget { value: string; label: string; team_id: string; squad_id: string | null; name: string; gender: TeamGender; squads: string[] }

/** « U18-1 et U18-2 », « A, B et C ». */
export const andList = (xs: readonly string[]): string =>
  xs.length > 1 ? `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}` : (xs[0] ?? '');

/** Une équipe a des équipes engagées à distinguer à partir de deux (une seule : c'est l'équipe). */
export const hasSquads = (t: MentorTeam): boolean => t.squads.length > 1;

/** « Une équipe » : chaque équipe engagée, et les équipes qui n'en ont pas (là, une équipe = toute l'équipe) ;
 *  « Toute l'équipe » : seulement les équipes qui ont plusieurs équipes engagées. */
export function mentorTargets(teams: readonly MentorTeam[]): { one: MentorTarget[]; whole: MentorTarget[] } {
  const one: MentorTarget[] = [], whole: MentorTarget[] = [];
  for (const t of teams) {
    const names = t.squads.map(s => s.name);
    if (hasSquads(t)) {
      for (const s of t.squads) one.push({ value: `s:${s.id}`, label: `${t.name} · ${s.name}`, team_id: t.team_id, squad_id: s.id, name: s.name, gender: t.gender, squads: [] });
      whole.push({ value: `t:${t.team_id}`, label: `${t.name} en entier (${andList(names)})`, team_id: t.team_id, squad_id: null, name: t.name, gender: t.gender, squads: names });
    } else {
      one.push({ value: `t:${t.team_id}`, label: t.name, team_id: t.team_id, squad_id: null, name: t.name, gender: t.gender, squads: [] });
    }
  }
  return { one, whole };
}

export type MentorKind = 'person' | 'team' | 'whole';

/** La phrase sous le choix (maquettes) : « Sarah suit les matchs de U18-1 et encourage ses joueuses ; elles la voient
 *  sur leur accueil. » ; « Sarah suit les matchs de U18-1 et de U18-2 et encourage toutes leurs joueuses. ».
 *  `mentor` : son prénom ; pas encore choisi, une phrase sans genre (« Qui parraine… »). `mentorGender` : celui de son
 *  équipe (marraine : « la », parrain : « le »). */
export function mentorHint(o: { kind: MentorKind; mentor: string | null; mentorGender: TeamGender; target: MentorTarget | null; mentee: string | null }): string {
  const who = o.mentor ?? 'Qui parraine';
  const la = o.mentorGender === 'M' ? 'le' : 'la';
  if (o.kind === 'person') {
    const m = o.mentee ?? 'son binôme';
    if (!o.mentor) return `${who} suit les matchs de ${m} et l’encourage ; sa famille voit qui c’est sur son accueil.`;
    return o.mentee ? `${who} suit les matchs de ${m} et l’encourage ; ${m} et sa famille ${la} voient sur leur accueil.`
      : `${who} suit les matchs de ${m} et l’encourage ; sa famille ${la} voit sur son accueil.`;
  }
  const t = o.target;
  if (!t) return `${who} suit les matchs de l’équipe et l’encourage.`;
  if (o.kind === 'whole' && t.squads.length > 1) {
    return `${who} suit les matchs ${andList(t.squads.map(s => `de ${s}`))} et encourage ${agree(t.gender, 'tous leurs joueurs', 'toutes leurs joueuses')}.`;
  }
  const they = agree(t.gender, 'ses joueurs ; ils', 'ses joueuses ; elles');
  return o.mentor ? `${who} suit les matchs de ${t.name} et encourage ${they} ${la} voient sur leur accueil.`
    : `${who} suit les matchs de ${t.name} et encourage ${they} voient qui c’est sur leur accueil.`;
}

/** Une ligne de « Binômes en cours » : « Sarah → U18-1 · équipe engagée », « Inès → U18 filles en entier · U18-1 et
 *  U18-2 », « Tess → U13 filles · toute l’équipe », « Sarah → Kenza · U13 filles ». */
export function pairLine(p: { mentee_person_id: string | null; mentee_squad_id?: string | null; mentee_name?: string | null; mentee: string;
  team_name?: string | null; squad_name?: string | null; squads?: { name: string }[] | null }): { target: string; detail: string | null } {
  if (p.mentee_person_id) return { target: p.mentee_name ?? p.mentee, detail: p.team_name ?? null };
  if (p.mentee_squad_id) return { target: p.squad_name ?? p.mentee, detail: 'équipe engagée' };
  const sq = (p.squads ?? []).map(s => s.name);
  if (sq.length > 1) return { target: `${p.team_name ?? p.mentee} en entier`, detail: andList(sq) };
  return { target: p.team_name ?? p.mentee, detail: 'toute l’équipe' };
}
